/**
 * SlotSelector Component
 * Component for selecting available schedule slots
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { AccessTime, Person, LocationOn, Check } from '@mui/icons-material';
import { PrerequisiteBlock } from '../../../components';
import { scheduleSlotService } from '../../../services/scheduleSlotService';
import { ScheduleSlot, SlotStatus, formatTimeRange } from '../../../types/scheduleSlot';

interface SlotSelectorProps {
  date: string;
  companyId: number;
  value: number | null;
  onChange: (slotId: number | null, slot: ScheduleSlot | null) => void;
  onNoSlotsAvailable?: (noSlots: boolean) => void;
}

export const SlotSelector: React.FC<SlotSelectorProps> = ({
  date,
  companyId,
  value,
  onChange,
  onNoSlotsAvailable,
}) => {
  const [slots, setSlots] = useState<ScheduleSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (date && companyId) {
      loadAvailableSlots();
    } else {
      setSlots([]);
    }
  }, [date, companyId]);

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);

      const availableSlots = await scheduleSlotService.getAvailableSlots(date, { companyId });

      // Minimum scheduling window: 3 hours from now
      const now = new Date();
      const minTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
      const [year, month, day] = date.split('-').map(Number);

      // Filter only truly available slots (and at least 3h from now)
      const filteredSlots = availableSlots.filter((slot) => {
        if (!slot.isActive || !slot.isAvailable ||
            slot.status === SlotStatus.BLOQUEADO ||
            slot.status === SlotStatus.CANCELADO) {
          return false;
        }
        const [slotH, slotM] = slot.startTime.split(':').map(Number);
        const slotDateTime = new Date(year, month - 1, day, slotH, slotM);
        return slotDateTime >= minTime;
      });

      setSlots(filteredSlots);

      // Notify parent about slot availability
      if (onNoSlotsAvailable) {
        onNoSlotsAvailable(filteredSlots.length === 0);
      }
    } catch (err: unknown) {
      console.error('Error loading available slots:', err);
      setError('Erro ao carregar horários disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slotId: number) => {
    const selectedSlot = slots.find((s) => s.id === slotId) || null;
    onChange(slotId, selectedSlot);
  };

  if (!date || !companyId) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Selecione uma data e empresa para ver os horários disponíveis
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (slots.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <PrerequisiteBlock
          title="Nenhum horário disponível para esta data"
          reason="Tente selecionar outra data ou abra novas vagas para esta clínica."
          resolve={{
            label: 'Abrir vagas',
            permission: 'schedule_slot.create',
            mode: 'navigate',
            route: '/schedule-slots',
            // Navegar aqui descartaria o restante do wizard já preenchido (empresa, exames).
            openInNewTab: true,
          }}
          deniedHint="Peça a um administrador para abrir vagas nesta agenda, ou tente outra data."
        />
      </Box>
    );
  }

  const isSelected = (slotId: number) => value === slotId;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {slots.length} horário(s) disponível(eis) para {new Date(date + 'T12:00:00').toLocaleDateString('pt-BR')}
      </Typography>
      <RadioGroup value={value?.toString() || ''}>
        <Grid container spacing={2}>
          {slots.map((slot) => (
            <Grid
              key={slot.id}
              size={{
                xs: 12,
                md: 6
              }}>
              <Card
                variant="outlined"
                sx={{
                  border: isSelected(slot.id) ? '2px solid' : '1px solid',
                  borderColor: isSelected(slot.id) ? 'success.main' : 'divider',
                  bgcolor: isSelected(slot.id) ? 'success.50' : 'background.paper',
                  transition: 'all 0.2s',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <FormControlLabel
                    value={slot.id.toString()}
                    control={<Radio size="small" />}
                    label=""
                    sx={{ position: 'absolute', opacity: 0 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTime fontSize="small" color="primary" />
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formatTimeRange(slot.startTime, slot.endTime)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={slot.appointmentType}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {slot.doctorName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {slot.location}
                    </Typography>
                  </Box>

                  {isSelected(slot.id) ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<Check />}
                      disabled
                    >
                      Selecionado
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleSlotSelect(slot.id)}
                    >
                      Selecionar
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
    </Box>
  );
};

export default SlotSelector;
